import { forwardRef, useState, type ReactNode } from "react";
import { Bell, Building2, CircleHelp, IdCard, LogOut, Menu, Search, Settings } from "lucide-react";

import { Popover, ThemeSwitcher } from "@/components/ds";
import { cn } from "@/lib/cn";

// The app bar above the content column. From d2d-UEM ds-uem/. SIEM's equivalent is an
// app component (Topbar.tsx), not part of its ds/, so this is built for the
// cascade plan (task 2.9) from SIEM's rc-topbar styles, renamed rc-appheader.
//
// The props are @datasirpi/ui's AppHeader, name for name, so Phase 3 can swap
// the import without touching Layout.tsx. Visual choices follow SIEM's bar:
// quiet 4%-ink buttons, one 64px row sharing the rail's hairline, and the
// account menu holding theme, settings and sign-out, as SIEM's Topbar does.
// The bar carries no page title: each page states its own at the top of its
// content, so the bar is identical on every route. The help button and the
// account button carry the product tour's data-tour hooks.

export interface AppHeaderUser {
  name: string;
  /** Shown under the name in the account menu, as SIEM's role tag. */
  role?: string;
  /** Optional avatar image; falls back to the name's initials. */
  avatarUrl?: string;
}

export interface AppHeaderProps {
  /** Title on the left (app or section name). */
  title?: ReactNode;
  /** Size of the title. Default "lg". */
  titleSize?: "sm" | "md" | "lg";
  /** Optional brand mark rendered before the title. */
  brand?: ReactNode;
  /** Show the search field. Default true. */
  showSearch?: boolean;
  searchPlaceholder?: string;
  /** Centered (default) or pinned after the title. */
  searchAlign?: "center" | "start";
  onSearch?: (value: string) => void;
  /** Show the notifications button. Default true. */
  showNotifications?: boolean;
  /** Unread count, shown as a badge when above 0. */
  notificationCount?: number;
  onNotificationsClick?: () => void;
  /** Show the help button. Default true. */
  showHelp?: boolean;
  onHelpClick?: () => void;
  /** Show the light/dark switcher in the account menu. Default true. */
  showThemeSwitcher?: boolean;
  /** Current user: drives the avatar and the account menu. Omit to hide both. */
  user?: AppHeaderUser;
  onProfile?: () => void;
  onLogout?: () => void;
  /** Show the profile item in the account menu. Default true. */
  showProfile?: boolean;
  /** Show the sign-out item in the account menu. Default true. */
  showLogout?: boolean;
  searchLabel?: string;
  notificationsLabel?: string;
  helpLabel?: string;
  themeLabel?: string;
  profileLabel?: string;
  logoutLabel?: string;
  /** Renders the menu button (below lg) that opens the sidebar. */
  onToggleSidebar?: () => void;
  /**
   * Where and as whom the user is working (ZTA design eval F25): the tenant
   * and the active role, always visible in the bar rather than only inside
   * the account menu. Either may be omitted.
   */
  context?: { tenant?: string | null; role?: string | null };
  "aria-label"?: string;
  className?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const AppHeader = forwardRef<HTMLElement, AppHeaderProps>(function AppHeader(
  {
    title,
    titleSize = "lg",
    brand,
    showSearch = true,
    searchPlaceholder = "Search",
    searchAlign = "center",
    onSearch,
    showNotifications = true,
    notificationCount = 0,
    onNotificationsClick,
    showHelp = true,
    onHelpClick,
    showThemeSwitcher = true,
    user,
    onProfile,
    onLogout,
    showProfile = true,
    showLogout = true,
    searchLabel,
    notificationsLabel = "Notifications",
    helpLabel = "Help",
    themeLabel = "Theme",
    profileLabel = "Profile",
    logoutLabel = "Sign out",
    onToggleSidebar,
    context,
    "aria-label": ariaLabel = "Application header",
    className,
  },
  ref
) {
  const [query, setQuery] = useState("");
  const unread = notificationCount > 0;
  const hasMenu = user && (showThemeSwitcher || (showProfile && onProfile) || (showLogout && onLogout));

  const search = showSearch ? (
    <label className={cn("rc-appheader__search", searchAlign === "center" && "rc-appheader__search--center")}>
      <Search aria-hidden="true" />
      <input
        type="search"
        value={query}
        placeholder={searchPlaceholder}
        aria-label={searchLabel ?? searchPlaceholder}
        onChange={(e) => {
          setQuery(e.target.value);
          onSearch?.(e.target.value);
        }}
      />
    </label>
  ) : null;

  return (
    <header ref={ref} className={cn("rc-appheader", className)} aria-label={ariaLabel}>
      <div className="rc-appheader__inner">
        <div className="rc-appheader__left">
          {onToggleSidebar ? (
            <button
              type="button"
              className="rc-appheader__button rc-appheader__button--icon rc-appheader__menu-toggle"
              aria-label="Open navigation"
              onClick={onToggleSidebar}
            >
              <Menu aria-hidden="true" />
            </button>
          ) : null}
          {brand ? <span className="rc-appheader__brand">{brand}</span> : null}
          {title ? (
            <h1 className="rc-appheader__title" data-size={titleSize}>
              {title}
            </h1>
          ) : null}
          {searchAlign === "start" ? search : null}
        </div>

        {searchAlign === "center" ? search : null}

        <div className="rc-appheader__right">
          {context && (context.tenant || context.role) ? (
            <span
              className="hidden min-w-0 items-center gap-1.5 text-sm text-[var(--d2d-text-secondary)] sm:inline-flex"
              data-testid="appheader-context"
            >
              {context.tenant ? (
                <span className="inline-flex min-w-0 items-center gap-1">
                  <span className="sr-only">Tenant </span>
                  <Building2 aria-hidden="true" className="h-4 w-4 flex-none text-[var(--d2d-text-muted)]" />
                  <span className="max-w-[16ch] truncate font-medium text-[var(--d2d-text-primary)]">{context.tenant}</span>
                </span>
              ) : null}
              {context.tenant && context.role ? <span aria-hidden="true">·</span> : null}
              {context.role ? (
                <span className="inline-flex min-w-0 items-center gap-1">
                  <span className="sr-only">Role </span>
                  <IdCard aria-hidden="true" className="h-4 w-4 flex-none text-[var(--d2d-text-muted)]" />
                  <span className="max-w-[20ch] truncate">{context.role}</span>
                </span>
              ) : null}
            </span>
          ) : null}
          {showNotifications ? (
            <button
              type="button"
              className="rc-appheader__button rc-appheader__button--icon"
              aria-label={unread ? `${notificationsLabel}, ${notificationCount} unread` : notificationsLabel}
              onClick={onNotificationsClick}
            >
              <Bell aria-hidden="true" />
              {unread ? (
                <span className="rc-appheader__badge" aria-hidden="true">
                  {notificationCount > 99 ? "99+" : notificationCount}
                </span>
              ) : null}
            </button>
          ) : null}

          {showHelp ? (
            <button
              type="button"
              className="rc-appheader__button rc-appheader__button--icon"
              aria-label={helpLabel}
              data-tour="help"
              onClick={onHelpClick}
            >
              <CircleHelp aria-hidden="true" />
            </button>
          ) : null}

          {user && hasMenu ? (
            <Popover
              side="bottom"
              align="end"
              content={
                <div className="rc-appheader__menu">
                  <div className="rc-appheader__menu-header">
                    <span className="rc-appheader__menu-name">{user.name}</span>
                    {user.role ? <span className="rc-appheader__menu-role">{user.role}</span> : null}
                  </div>
                  <div className="rc-appheader__menu-divider" role="separator" />
                  {showThemeSwitcher ? (
                    <div className="rc-appheader__menu-row rc-appheader__menu-row--static">
                      <span>Theme</span>
                      <ThemeSwitcher aria-label={themeLabel} />
                    </div>
                  ) : null}
                  {showProfile && onProfile ? (
                    <button type="button" className="rc-appheader__menu-row" onClick={onProfile}>
                      <span>{profileLabel}</span>
                      <Settings aria-hidden="true" />
                    </button>
                  ) : null}
                  {showLogout && onLogout ? (
                    <>
                      <div className="rc-appheader__menu-divider" role="separator" />
                      <button type="button" className="rc-appheader__menu-row" onClick={onLogout}>
                        <span>{logoutLabel}</span>
                        <LogOut aria-hidden="true" />
                      </button>
                    </>
                  ) : null}
                </div>
              }
            >
              <button type="button" className="rc-appheader__button" aria-label={`Account menu, ${user.name}`} data-tour="theme">
                <span className="rc-appheader__avatar" aria-hidden="true">
                  {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials(user.name)}
                </span>
                <span className="rc-appheader__user-name">{user.name}</span>
              </button>
            </Popover>
          ) : user ? (
            <span className="rc-appheader__button" aria-label={user.name}>
              <span className="rc-appheader__avatar" aria-hidden="true">
                {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials(user.name)}
              </span>
              <span className="rc-appheader__user-name">{user.name}</span>
            </span>
          ) : null}
        </div>
      </div>
    </header>
  );
});
